import React, { useCallback, useEffect, useState } from 'react';
import { getProtectionSettings, saveProtectionSettings, errorMessage } from '../api/securityApi';
import SecurityNav from '../components/Security/SecurityNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import RequirePermission from '../auth/RequirePermission';
import { hasPermission } from '../auth/permissions';

const PRESETS = {
  Low:    { blockThreshold: 90, monitorThreshold: 60, maxClicksPerWindow: 8, clickWindowSeconds: 60, maxSessionsPerIpPerMinute: 30, minTimeToFirstClickMs: 150, maxIpsPerFingerprint: 8, maxFingerprintsPerIp: 12 },
  Medium: { blockThreshold: 80, monitorThreshold: 45, maxClicksPerWindow: 5, clickWindowSeconds: 60, maxSessionsPerIpPerMinute: 20, minTimeToFirstClickMs: 300, maxIpsPerFingerprint: 5, maxFingerprintsPerIp: 8 },
  High:   { blockThreshold: 65, monitorThreshold: 35, maxClicksPerWindow: 3, clickWindowSeconds: 60, maxSessionsPerIpPerMinute: 12, minTimeToFirstClickMs: 500, maxIpsPerFingerprint: 3, maxFingerprintsPerIp: 5 },
};

const NUM = [
  ['maxEventsPerIpPerMinute', 'Max events per IP per minute (flood limit, min 30)'],
  ['blockThreshold', 'Block threshold (risk score)'],
  ['monitorThreshold', 'Monitor threshold (risk score)'],
  ['maxClicksPerWindow', 'Max clicks per window'],
  ['clickWindowSeconds', 'Click window (seconds)'],
  ['maxSessionsPerIpPerMinute', 'Max sessions per IP / minute'],
  ['minTimeToFirstClickMs', 'Min time to first click (ms)'],
  ['maxIpsPerFingerprint', 'Max IPs per fingerprint'],
  ['maxFingerprintsPerIp', 'Max fingerprints per IP'],
  ['autoBlockMinutes', 'Auto-block duration (minutes)'],
];

export default function ProtectionSettingsPage() {
  const { sites, siteId, setSiteId, ready } = useActiveSite();
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [saving, setSaving] = useState(false);
  const canEdit = hasPermission('protection.update');

  const load = useCallback(async () => {
    if (!siteId) { setS(null); return; }
    setError('');
    try { setS(await getProtectionSettings(siteId)); } catch (e) { setError(errorMessage(e)); }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (ready && !siteId && sites.length) {
      const first = sites.find((x) => x.activePublicKey);
      if (first) setSiteId(first.activePublicKey);
    }
  }, [ready, siteId, sites, setSiteId]);

  const save = async () => {
    setSaving(true); setError(''); setInfo('');
    try { setS(await saveProtectionSettings(siteId, s)); setInfo('Settings saved.'); }
    catch (e) { setError(errorMessage(e)); }
    finally { setSaving(false); }
  };
  const preset = (name) => setS({ ...s, ...PRESETS[name], protectionMode: name });

  return (
    <div className="shell">
      <header className="page-header"><div><h1>Protection settings</h1>
        <p className="muted">Tune how aggressively invalid traffic is flagged for each site.</p></div></header>
      <SecurityNav />
      <div className="sec-toolbar"><SiteSelector sites={sites} value={siteId} onChange={setSiteId} allowAll={false} /></div>
      {error && <div className="toast error">{error}</div>}
      {info && <div className="toast info">{info}</div>}
      {!siteId && <div className="sec-card">Select a site to edit its protection settings.</div>}

      {s && (
        <>
          {!s.enforceBlocking && (
            <div className="sec-warn"><b>Shadow mode is on.</b> Threats are detected and logged, but visitors are not blocked yet.
              Review the Threats page for a few days, then enable enforcement below. Your blocklist is always enforced.</div>
          )}
          {s.isInherited && <p className="muted">These are the default settings. Saving creates site-specific settings.</p>}

          <div className="sec-card">
            <h3>Protection level</h3>
            <div className="sec-row">
              {Object.keys(PRESETS).map((p) => (
                <button key={p} disabled={!canEdit} className={s.protectionMode === p ? 'primary' : 'ghost'} onClick={() => preset(p)}>{p}</button>
              ))}
              <span className="muted">Current: {s.protectionMode}</span>
            </div>
          </div>

          <div className="sec-card" style={{ marginTop: 16 }}>
            <h3>Behaviour</h3>
            {[
              ['enforceBlocking', 'Enforce blocking (turn off for shadow mode)'],
              ['autoBlock', 'Auto-add high-risk IPs to the blocklist'],
              ['detectBots', 'Detect bots and headless browsers'],
            ].map(([k, label]) => (
              <label key={k} style={{ display: 'block', margin: '8px 0' }}>
                <input type="checkbox" disabled={!canEdit} checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} /> {label}
              </label>
            ))}
            <h3 style={{ marginTop: 18 }}>Thresholds</h3>
            {NUM.map(([k, label]) => (
              <div className="sec-field" key={k}>
                <label htmlFor={k}>{label}</label>
                <input id={k} className="sec-input" type="number" disabled={!canEdit} value={s[k]} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} />
              </div>
            ))}
            <h3 style={{ marginTop: 18 }}>Active defences (plugin v2)</h3>
            <p className="muted">These act on visitors in the browser. Turn on enforcement first; each one only applies to visitors who are already being blocked or challenged.</p>
            {[
              ['challengeEnabled', 'Proof-of-work challenge: suspicious sessions must solve a small puzzle; bots that skip it are flagged'],
              ['hideAdsOnBlock', 'Hide ad slots for blocked visitors'],
              ['honeypotEnabled', 'Honeypot link: invisible trap that only bots follow'],
            ].map(([k, label]) => (
              <label key={k} style={{ display: 'block', margin: '8px 0' }}>
                <input type="checkbox" disabled={!canEdit} checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} /> {label}
              </label>
            ))}
            <div className="sec-field"><label htmlFor="redirectUrl">Redirect blocked visitors to (optional URL)</label>
              <input id="redirectUrl" className="sec-input" disabled={!canEdit} placeholder="https://example.com/blocked" value={s.redirectUrl || ''} onChange={(e) => setS({ ...s, redirectUrl: e.target.value })} /></div>
            <div className="sec-field"><label htmlFor="captchaProvider">CAPTCHA for borderline visitors</label>
              <select id="captchaProvider" className="sec-select" disabled={!canEdit} value={s.captchaProvider || 'None'} onChange={(e) => setS({ ...s, captchaProvider: e.target.value })}>
                {['None', 'Turnstile', 'Recaptcha', 'Hcaptcha'].map((p) => <option key={p}>{p}</option>)}</select></div>
            {(s.captchaProvider || 'None') !== 'None' && (
              <>
                <div className="sec-field"><label htmlFor="captchaSiteKey">CAPTCHA site key</label>
                  <input id="captchaSiteKey" className="sec-input" disabled={!canEdit} value={s.captchaSiteKey || ''} onChange={(e) => setS({ ...s, captchaSiteKey: e.target.value })} /></div>
                <div className="sec-field"><label htmlFor="captchaSecret">CAPTCHA secret {s.captchaSecretSet ? '(saved; leave blank to keep)' : ''}</label>
                  <input id="captchaSecret" className="sec-input" type="password" autoComplete="new-password" disabled={!canEdit} value={s.captchaSecret || ''} onChange={(e) => setS({ ...s, captchaSecret: e.target.value })} /></div>
              </>)}
            <RequirePermission code="protection.update">
              <button className="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
            </RequirePermission>
          </div>
        </>
      )}
    </div>
  );
}
